/**
 * storageService - Abstraction layer for browser storage
 *
 * Detects if localStorage is available and persistent.
 * If not (disabled, blocked, or private mode with no persistence),
 * falls back to sessionStorage for the current session.
 *
 * For contacts and preferences, the server file is always the source
 * of truth when localStorage is unavailable.
 */

// ─── Detection ────────────────────────────────────────────────────────────────

const checkLocalStorage = () => {
    try {
        const key = '__cabaza_ls_test__';
        localStorage.setItem(key, '1');
        const ok = localStorage.getItem(key) === '1';
        localStorage.removeItem(key);
        return ok;
    } catch {
        return false;
    }
};

// Evaluated once at module load time
let _localStorageAvailable = checkLocalStorage();

/**
 * Returns true if localStorage is available and persistent.
 * Call this to decide whether to use local cache or always hit the server.
 */
export const isStoragePersistent = () => _localStorageAvailable;

/**
 * Re-evaluate localStorage availability (call after a permission change).
 */
export const recheckStorage = () => {
    _localStorageAvailable = checkLocalStorage();
    if (!_localStorageAvailable) {
        console.warn('[storage] localStorage unavailable — falling back to sessionStorage. Contacts and preferences will be loaded from server file.');
    }
    return _localStorageAvailable;
};

// ─── Unified storage API ──────────────────────────────────────────────────────

const _backend = () => (_localStorageAvailable ? localStorage : sessionStorage);

export const storage = {
    getItem: (key) => {
        try {
            return _backend().getItem(key);
        } catch {
            return null;
        }
    },
    setItem: (key, value) => {
        try {
            _backend().setItem(key, value);
        } catch (e) {
            console.warn('[storage] setItem failed:', key, e);
        }
    },
    removeItem: (key) => {
        try {
            _backend().removeItem(key);
        } catch {
            // ignore
        }
    },
    clear: () => {
        try {
            _backend().clear();
        } catch {
            // ignore
        }
    }
};

// Log storage mode once on load
if (!_localStorageAvailable) {
    console.warn('[storage] localStorage unavailable at startup — using sessionStorage. Server file will be used as primary source for contacts and preferences.');
}
